import React, { ReactNode, RefObject } from "react";
import Page from "../../page-layout";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { IonInfiniteScroll, IonInfiniteScrollContent, IonSkeletonText } from "@ionic/react";
import { BackButton, NewListViewProduct } from "@core/components";
import { Image } from "../../../components/Image/Image";
import { AdditionalFeature, Product } from "@core/types";
import SearchButton from "../../../parhato2/src/entities/ui/search-button";
import CategorySkeleton from "../../../components/UI/CategorySkeleton";
import NoProductsFound from "../../../parhato2/src/entities/ui/no-products-found";
import classNames from "classnames/bind";
import styles from "./Template.module.scss";
import CategoryFilters from "../../../parhato2/src/entities/CategoryFilters";
import PullToRefresh from "../../../parhato2/src/entities/pullToRefresh";
import { useTranslation } from "react-i18next";

interface TemplatePageProps {
  // Page data
  title: string;
  description?: string | ReactNode;
  imageSrc: string;
  base64Image?: string;
  // Products
  products?: Product[];
  hasProducts: boolean;

  // Loading state
  isLoading: boolean;
  isRefetchingProducts?: boolean;

  // Infinite scroll
  infiniteRef: RefObject<HTMLIonInfiniteScrollElement>;
  isInfiniteScrollDisabled?: boolean;
  onIonInfinite: () => Promise<void>;
  hasNextPage?: boolean;
  onShowMoreClick?: () => void;

  // Optional components
  filters?: AdditionalFeature[];
  customProductList?: ReactNode;
  customHeader?: ReactNode;
  className?: string;

  // Additional options
  showSearchButton?: boolean;
  isFetchingNextPage?: boolean;

  showNoFoundProducts?: boolean;
}

const cx = classNames.bind(styles);

const TemplatePage: React.FC<TemplatePageProps> = ({
  title,
  description,
  imageSrc,
  base64Image,
  products,
  hasProducts,
  isLoading,
  isRefetchingProducts,
  infiniteRef,
  isInfiniteScrollDisabled = false,
  onIonInfinite,
  filters,
  customHeader,
  className,
  showSearchButton = true,
  isFetchingNextPage,
  onShowMoreClick,
  hasNextPage,
  showNoFoundProducts = true,
}) => {
  const { t } = useTranslation();

  const FilterSkeleton = () => {
    return (
      <div className="flex gap-2.5 pl-5 pr-5 w-full h-8">
        <IonSkeletonText className="w-[7.1875rem] h-8 rounded-2xl" animated />
        <IonSkeletonText className="w-[8.1875rem] h-8 rounded-2xl" animated />
        <IonSkeletonText className="w-[5.42rem] h-8 rounded-2xl" animated />
        <IonSkeletonText className="w-[8rem] h-8 rounded-2xl" animated />
        <IonSkeletonText className="w-[3rem] h-8 rounded-2xl" animated />
      </div>
    );
  };

  return (
    <Page>
      <Page.Header className="pt-[env(safe-area-inset-top)] ">
        <MaxWidthContainer classList="!sticky top-0 z-50 bg-white h-16">
          {customHeader || (
            <div className="w-full flex items-center justify-center h-[4rem] px-2.5">
              <BackButton />
              <div className="flex-1 text-center text-[1.125rem] font-bold truncate mx-1">{title}</div>
              <div className="w-[4rem] h-[2rem]">{showSearchButton && <SearchButton />}</div>
            </div>
          )}
        </MaxWidthContainer>
      </Page.Header>

      <Page.Content>
        <PullToRefresh />
        {isLoading ? (
          <CategorySkeleton />
        ) : (
          <MaxWidthContainer
            classList={cx(!hasProducts && "flex flex-col justify-center items-center h-full overflow-hidden")}
          >
            {imageSrc && hasProducts && (
              <div className={cx("hero", "md:hidden")}>
                <Image src={imageSrc} placeholder={base64Image} className={cx("image", className)} />
              </div>
            )}

            <div className={cx("content", imageSrc && hasProducts && "contentWithHero")}>
              <div className="w-full flex flex-col gap-2.5 text-lg">
                {description && (
                  <div className="px-5">
                    <>
                      {typeof description === "string" ? (
                        <div className="flex flex-col gap-2.5 rounded-3xl p-5 pt-0 mx-5">
                          <p className="text-sm font-bold">{description}</p>
                        </div>
                      ) : (
                        description
                      )}
                    </>
                  </div>
                )}

                {isRefetchingProducts && !filters && <FilterSkeleton />}

                {!!filters?.length && <CategoryFilters categoriesFilters={filters} />}
              </div>

              {hasProducts ? (
                <>
                  <div className="flex flex-col gap-2 p-5">
                    <div className="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-4 gap-y-2.5 gap-x-2.5">
                      {products?.map((product) => (
                        <NewListViewProduct
                          key={product.id}
                          product={product}
                          type="newList"
                          lazyload={false}
                          className=""
                        />
                      ))}
                    </div>
                  </div>
                  <div
                    className="flex flex-col items-center justify-center gap-2 mt-5 pb-8"
                    style={{ visibility: hasNextPage ? "visible" : "hidden" }}
                  >
                    <button
                      disabled={isFetchingNextPage}
                      onClick={onShowMoreClick}
                      className="flex items-center px-14 py-3 rounded-xl bg-primary"
                    >
                      {t("Show more")}
                    </button>
                  </div>
                  <IonInfiniteScroll
                    className="flex items-center justify-center mt-4 pb-8"
                    disabled={isInfiniteScrollDisabled}
                    threshold="100px"
                    ref={infiniteRef}
                    onIonInfinite={onIonInfinite}
                  >
                    <IonInfiniteScrollContent loadingSpinner="lines" />
                  </IonInfiniteScroll>
                </>
              ) : (
                showNoFoundProducts && (
                  <div className="flex flex-col justify-center items-center min-h-[50vh] pb-20">
                    <NoProductsFound />
                  </div>
                )
              )}
            </div>
          </MaxWidthContainer>
        )}
      </Page.Content>
    </Page>
  );
};

export default TemplatePage;
